<template>
    <div class="kv-color-input">
        <KvPopup
            class="kv-color-input-popup"
            :placement="placement"
            min-width="260px"
            @close-outside="handleClose"
        >
            <template #trigger="{ toggle }">
                <div class="kv-color-input-trigger" @click="!disabled && toggle()">
                    <KvInput
                        :id="id"
                        :value="normalizedValue"
                        :label="label"
                        :variant="variant"
                        :size="size"
                        :disabled="disabled"
                        :readonly="true"
                        append-icon="ui-chevron-down"
                        class="kv-color-trigger-input"
                    >
                        <template #prepend>
                            <span
                                class="kv-color-dot"
                                :style="{ backgroundColor: normalizedValue }"
                                aria-hidden="true"
                            ></span>
                        </template>
                    </KvInput>
                </div>
            </template>

            <template #content="{ toggle }">
                <div
                    class="kv-color-panel"
                    role="dialog"
                    :aria-label="t('inputs-color-title')"
                    @keydown.esc.stop="toggle(false)"
                >
                    <h3>{{ t("inputs-color-title") }}</h3>

                    <div class="kv-color-controls">
                        <div
                            ref="colorArea"
                            class="kv-color-area"
                            :style="{ backgroundColor: hueColor }"
                            role="slider"
                            tabindex="0"
                            :aria-label="t('inputs-color-saturation_brightness')"
                            :aria-valuetext="saturationBrightnessText"
                            @mousedown="startColorDrag"
                            @touchstart="startColorDrag"
                            @keydown="onColorAreaKeydown"
                        >
                            <span
                                class="kv-color-area-handle"
                                :style="{
                                    backgroundColor: selectedHex,
                                    left: `${hsv.s}%`,
                                    top: `${100 - hsv.v}%`,
                                }"
                            ></span>
                        </div>

                        <div
                            ref="hueSlider"
                            class="kv-color-hue"
                            role="slider"
                            tabindex="0"
                            :aria-label="t('inputs-color-hue')"
                            aria-valuemin="0"
                            aria-valuemax="360"
                            :aria-valuenow="hsv.h"
                            @mousedown="startHueDrag"
                            @touchstart="startHueDrag"
                            @keydown="onHueKeydown"
                        >
                            <span
                                class="kv-color-hue-handle"
                                :style="{
                                    backgroundColor: hueColor,
                                    left: `${huePosition}%`,
                                }"
                            ></span>
                        </div>

                        <KvInput
                            :value="hexInput"
                            variant="outlined"
                            size="small"
                            :has-error="hexInputInvalid"
                            :placeholder="t('inputs-color-hex')"
                            @input="onHexInput"
                            @change="commitHexInput"
                            @blur="commitHexInput"
                        />
                    </div>

                    <div class="kv-color-basics">
                        <h4>{{ t("inputs-color-basics") }}</h4>

                        <div class="kv-color-swatches">
                            <button
                                v-for="color in basicColors"
                                :key="color"
                                type="button"
                                class="kv-color-swatch"
                                :class="{ active: normalizedValue.toLowerCase() === color.toLowerCase() }"
                                :style="{ backgroundColor: color }"
                                :aria-label="color"
                                @click="selectColor(color)"
                            ></button>
                        </div>
                    </div>
                </div>
            </template>
        </KvPopup>
    </div>
</template>

<script>
import KvPopup from "../ui/kv-popup.vue";
import KvInput from "./kv-input.vue";

const DEFAULT_COLOR = "#2AAA4C";

export default {
    name: "KvColorInput",
    components: {
        KvPopup,
        KvInput,
    },
    model: {
        prop: "value",
        event: "input",
    },
    props: {
        value: {
            type: String,
            default: DEFAULT_COLOR,
        },
        label: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default() {
                return "kv-color-input-" + Math.random().toString(36).substr(2, 9);
            },
        },
        variant: {
            type: String,
            default: "outlined",
        },
        size: {
            type: String,
            default: "regular",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placement: {
            type: String,
            default: "bottom left",
        },
    },
    data() {
        return {
            dragMode: null,
            hexInput: DEFAULT_COLOR,
            hsv: {
                h: 145,
                s: 77,
                v: 67,
            },
            basicColors: [
                "#000000",
                "#007AFF",
                "#34C759",
                "#FFCC00",
                "#FF3B30",
                "#AF52DE",
                "#5856D6",
                "#FF2D55",
            ],
        };
    },
    computed: {
        normalizedValue() {
            return this.normalizeHex(this.value) || this.selectedHex || DEFAULT_COLOR;
        },
        selectedHex() {
            return this.hsvToHex(this.hsv);
        },
        hueColor() {
            return this.hsvToHex({ h: this.hsv.h, s: 100, v: 100 });
        },
        huePosition() {
            return (this.hsv.h / 360) * 100;
        },
        hexInputInvalid() {
            return Boolean(this.hexInput) && !this.isValidHexInput(this.hexInput);
        },
        saturationBrightnessText() {
            return this.t("inputs-color-saturation_brightness_value", [
                this.hsv.s,
                this.hsv.v,
            ]);
        },
    },
    watch: {
        value(value) {
            const normalized = this.normalizeHex(value);
            if (normalized && normalized !== this.selectedHex) {
                this.applyHex(normalized, false);
            }
        },
    },
    created() {
        this.applyHex(this.normalizedValue, false);
    },
    beforeDestroy() {
        this.removeDragListeners();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        handleClose() {
            this.removeDragListeners();
        },
        normalizeHex(raw) {
            if (!raw) return null;

            let value = String(raw).trim();
            if (!value.startsWith("#")) value = `#${value}`;

            const short = value.match(/^#([0-9a-f]{3})$/i);
            if (short) {
                const chars = short[1];
                value = `#${chars[0]}${chars[0]}${chars[1]}${chars[1]}${chars[2]}${chars[2]}`;
            }

            if (!/^#[0-9a-f]{6}$/i.test(value)) return null;

            return value.toUpperCase();
        },
        isValidHexInput(raw) {
            if (!raw) return false;
            const value = String(raw).trim();
            return /^#?[0-9a-f]{3}$/i.test(value) || /^#?[0-9a-f]{6}$/i.test(value);
        },
        clamp(value, min, max) {
            return Math.min(max, Math.max(min, value));
        },
        hsvToHex({ h, s, v }) {
            const hue = ((Number(h) % 360) + 360) % 360;
            const saturation = this.clamp(Number(s), 0, 100) / 100;
            const value = this.clamp(Number(v), 0, 100) / 100;
            const chroma = value * saturation;
            const segment = hue / 60;
            const x = chroma * (1 - Math.abs((segment % 2) - 1));
            const match = value - chroma;
            let r = 0;
            let g = 0;
            let b = 0;

            if (segment >= 0 && segment < 1) {
                r = chroma;
                g = x;
            } else if (segment >= 1 && segment < 2) {
                r = x;
                g = chroma;
            } else if (segment >= 2 && segment < 3) {
                g = chroma;
                b = x;
            } else if (segment >= 3 && segment < 4) {
                g = x;
                b = chroma;
            } else if (segment >= 4 && segment < 5) {
                r = x;
                b = chroma;
            } else {
                r = chroma;
                b = x;
            }

            return `#${[r, g, b]
                .map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, "0"))
                .join("")
                .toUpperCase()}`;
        },
        hexToHsv(hex) {
            const normalized = this.normalizeHex(hex) || DEFAULT_COLOR;
            const r = parseInt(normalized.substring(1, 3), 16) / 255;
            const g = parseInt(normalized.substring(3, 5), 16) / 255;
            const b = parseInt(normalized.substring(5, 7), 16) / 255;
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const delta = max - min;
            let h = 0;

            if (delta !== 0) {
                if (max === r) h = 60 * (((g - b) / delta) % 6);
                if (max === g) h = 60 * ((b - r) / delta + 2);
                if (max === b) h = 60 * ((r - g) / delta + 4);
            }

            if (h < 0) h += 360;

            return {
                h: Math.round(h),
                s: max === 0 ? 0 : Math.round((delta / max) * 100),
                v: Math.round(max * 100),
            };
        },
        setHsv(nextHsv, shouldEmit = true) {
            this.hsv = {
                h: this.clamp(Math.round(nextHsv.h), 0, 360),
                s: this.clamp(Math.round(nextHsv.s), 0, 100),
                v: this.clamp(Math.round(nextHsv.v), 0, 100),
            };
            this.hexInput = this.selectedHex;
            if (shouldEmit) this.emitColor(this.selectedHex);
        },
        applyHex(color, shouldEmit = true) {
            const normalized = this.normalizeHex(color);
            if (!normalized) return;

            this.hsv = this.hexToHsv(normalized);
            this.hexInput = normalized;
            if (shouldEmit) this.emitColor(normalized);
        },
        emitColor(color) {
            const normalized = this.normalizeHex(color);
            if (normalized) {
                this.$emit("input", normalized);
            }
        },
        onHexInput(value) {
            this.hexInput = String(value || "").toUpperCase();
            const normalized = this.normalizeHex(this.hexInput);
            if (normalized) {
                this.applyHex(normalized);
            }
        },
        commitHexInput() {
            const normalized = this.normalizeHex(this.hexInput);
            if (normalized) {
                this.applyHex(normalized);
            } else {
                this.hexInput = this.selectedHex;
            }
        },
        selectColor(color) {
            this.applyHex(color);
        },
        getEventPoint(event) {
            const touch = event.touches?.[0] || event.changedTouches?.[0];
            return touch || event;
        },
        getRelativePosition(refName, event) {
            const element = this.$refs[refName];
            if (!element) return { x: 0, y: 0 };

            const point = this.getEventPoint(event);
            const rect = element.getBoundingClientRect();
            return {
                x: this.clamp((point.clientX - rect.left) / rect.width, 0, 1),
                y: this.clamp((point.clientY - rect.top) / rect.height, 0, 1),
            };
        },
        startColorDrag(event) {
            this.dragMode = "color";
            this.updateColorFromEvent(event);
            this.addDragListeners();
        },
        startHueDrag(event) {
            this.dragMode = "hue";
            this.updateHueFromEvent(event);
            this.addDragListeners();
        },
        addDragListeners() {
            this.removeDragListeners();
            document.addEventListener("mousemove", this.onDocumentDrag);
            document.addEventListener("mouseup", this.stopDocumentDrag);
            document.addEventListener("touchmove", this.onDocumentDrag, { passive: false });
            document.addEventListener("touchend", this.stopDocumentDrag);
            document.addEventListener("touchcancel", this.stopDocumentDrag);
        },
        removeDragListeners() {
            document.removeEventListener("mousemove", this.onDocumentDrag);
            document.removeEventListener("mouseup", this.stopDocumentDrag);
            document.removeEventListener("touchmove", this.onDocumentDrag);
            document.removeEventListener("touchend", this.stopDocumentDrag);
            document.removeEventListener("touchcancel", this.stopDocumentDrag);
        },
        onDocumentDrag(event) {
            if (!this.dragMode) return;
            if (this.dragMode === "color") this.updateColorFromEvent(event);
            if (this.dragMode === "hue") this.updateHueFromEvent(event);
        },
        stopDocumentDrag() {
            this.dragMode = null;
            this.removeDragListeners();
        },
        updateColorFromEvent(event) {
            if (event.cancelable) event.preventDefault();
            const position = this.getRelativePosition("colorArea", event);
            this.setHsv({
                h: this.hsv.h,
                s: position.x * 100,
                v: (1 - position.y) * 100,
            });
        },
        updateHueFromEvent(event) {
            if (event.cancelable) event.preventDefault();
            const position = this.getRelativePosition("hueSlider", event);
            this.setHsv({
                h: position.x * 360,
                s: this.hsv.s,
                v: this.hsv.v,
            });
        },
        onColorAreaKeydown(event) {
            const step = event.shiftKey ? 10 : 1;
            const next = { ...this.hsv };

            if (event.key === "ArrowRight") next.s += step;
            else if (event.key === "ArrowLeft") next.s -= step;
            else if (event.key === "ArrowUp") next.v += step;
            else if (event.key === "ArrowDown") next.v -= step;
            else if (event.key === "Home") next.s = 0;
            else if (event.key === "End") next.s = 100;
            else return;

            event.preventDefault();
            this.setHsv(next);
        },
        onHueKeydown(event) {
            const step = event.shiftKey ? 10 : 1;
            const next = { ...this.hsv };

            if (event.key === "ArrowRight" || event.key === "ArrowUp") next.h += step;
            else if (event.key === "ArrowLeft" || event.key === "ArrowDown") next.h -= step;
            else if (event.key === "Home") next.h = 0;
            else if (event.key === "End") next.h = 360;
            else return;

            event.preventDefault();
            this.setHsv(next);
        },
    },
};
</script>

<style lang="less">
.kv-color-input {
    width: 100%;
}

.kv-color-input-popup {
    display: block;
    width: 100%;

    .kv-popup-container {
        display: block;
        width: 100%;
    }
}

.kv-color-input-trigger {
    cursor: pointer;
    width: 100%;
}

.kv-color-trigger-input {
    pointer-events: none;
    width: 100%;
}

.kv-color-dot {
    border: 1px solid #384250;
    border-radius: 50%;
    flex: 0 0 auto;
    height: 16px;
    width: 16px;
}

.kv-color-panel {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 4px;
    width: 260px;

    h3,
    h4 {
        color: #384250;
        font-family: "Sora", sans-serif;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.3;
        margin: 0;
    }
}

.kv-color-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.kv-color-area {
    aspect-ratio: 4 / 3;
    border: 1px solid #f3f4f6;
    border-radius: 8px;
    cursor: crosshair;
    overflow: hidden;
    position: relative;
    touch-action: none;
    width: 100%;

    &::before,
    &::after {
        content: "";
        inset: 0;
        pointer-events: none;
        position: absolute;
    }

    &::before {
        background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    }

    &::after {
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
    }

    &:focus-visible {
        outline: 2px solid #384250;
        outline-offset: 2px;
    }
}

.kv-color-area-handle,
.kv-color-hue-handle {
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.28);
    display: block;
    pointer-events: none;
    position: absolute;
    transform: translate(-50%, -50%);
    z-index: 2;
}

.kv-color-area-handle {
    height: 13px;
    width: 13px;
}

.kv-color-hue {
    background: linear-gradient(
        90deg,
        #ff0000 0%,
        #ffff00 16.66%,
        #00ff00 33.33%,
        #00ffff 50%,
        #0000ff 66.66%,
        #ff00ff 83.33%,
        #ff0000 100%
    );
    border: 1px solid #eeeeee;
    border-radius: 8px;
    cursor: pointer;
    height: 14px;
    position: relative;
    touch-action: none;
    width: 100%;

    &:focus-visible {
        outline: 2px solid #384250;
        outline-offset: 2px;
    }
}

.kv-color-hue-handle {
    height: 14px;
    top: 50%;
    width: 14px;
}

.kv-color-basics {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.kv-color-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.kv-color-swatch {
    border: 1px solid #eeeeee;
    border-radius: 4px;
    cursor: pointer;
    height: 22px;
    padding: 0;
    width: 22px;

    &.active {
        box-shadow: 0 0 0 2px #1f2a37;
    }

    &:focus-visible {
        outline: 2px solid #384250;
        outline-offset: 2px;
    }
}
</style>
